import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, MessageCircle, Phone, Star } from "lucide-react";
import client, { imgSrc } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { CATEGORY_LABELS, STORES, fmtPrice, waLink } from "@/lib/constants";

function Gallery({ images, name }) {
  const [active, setActive] = useState(0);
  const total = images.length;
  const go = (dir) => setActive((a) => (a + dir + total) % total);

  useEffect(() => {
    setActive(0);
  }, [images]);

  if (total === 0) return null;

  return (
    <div>
      <div className="relative">
        <div className="gold-spotlight inset-[-15%] h-[130%] w-[130%]" />
        <div className="group relative overflow-hidden bg-[#0a0a0a] p-0">
          <img
            key={images[active]}
            src={imgSrc(images[active])}
            alt={`${name} — foto ${active + 1}`}
            data-testid="gallery-main-image"
            className="aspect-square w-full object-cover"
          />
          {total > 1 && (
            <>
              <button
                onClick={() => go(-1)}
                data-testid="gallery-prev-button"
                aria-label="Foto precedente"
                className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center border border-white/15 bg-black/50 text-[#FAFAFA] backdrop-blur-sm transition-colors duration-300 hover:border-gold hover:text-gold"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => go(1)}
                data-testid="gallery-next-button"
                aria-label="Foto successiva"
                className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center border border-white/15 bg-black/50 text-[#FAFAFA] backdrop-blur-sm transition-colors duration-300 hover:border-gold hover:text-gold"
              >
                <ChevronRight size={18} />
              </button>
              <span
                className="absolute bottom-3 right-3 bg-black/60 px-3 py-1 text-[10px] uppercase tracking-[0.3em] text-[#FAFAFA] backdrop-blur-sm"
                data-testid="gallery-counter"
              >
                {active + 1} / {total}
              </span>
            </>
          )}
        </div>
      </div>
      {total > 1 && (
        <div className="mt-3 grid grid-cols-4 gap-3" data-testid="gallery-thumbnails">
          {images.map((img, i) => (
            <button
              key={img + i}
              onClick={() => setActive(i)}
              data-testid={`gallery-thumb-${i}`}
              aria-label={`Vai alla foto ${i + 1}`}
              className={`overflow-hidden bg-[#0a0a0a] transition-all duration-300 ${
                i === active ? "ring-1 ring-gold" : "opacity-50 hover:opacity-90"
              }`}
            >
              <img src={imgSrc(img)} alt="" loading="lazy" className="aspect-square w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    client
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        client
          .get("/products", { params: { category: r.data.category } })
          .then((rel) => setRelated(rel.data.filter((p) => p.id !== r.data.id).slice(0, 4)))
          .catch(() => {});
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <main className="mx-auto max-w-7xl px-5 pb-28 pt-40 text-center" data-testid="product-not-found">
        <p className="font-serif text-3xl italic text-[#A3A3A3]">Questo gioiello non è più disponibile online.</p>
        <Link to="/catalogo" className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-gold">
          <ArrowLeft size={14} /> Torna al catalogo
        </Link>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="mx-auto max-w-7xl px-5 pb-28 pt-40" data-testid="product-loading">
        <p className="text-sm uppercase tracking-[0.3em] text-[#737373]">Caricamento…</p>
      </main>
    );
  }

  const galleryImages = product.images?.length ? product.images : product.image ? [product.image] : [];
  const waText = `Buongiorno, vorrei informazioni su: ${product.name}${product.reference ? ` (Rif. ${product.reference})` : ""}, € ${fmtPrice(product.price)}. È ancora disponibile?`;

  return (
    <main className="mx-auto max-w-7xl px-5 pb-28 pt-28 md:px-8 md:pt-36" data-testid="product-detail-page">
      <Reveal>
        <Link
          to="/catalogo"
          data-testid="product-back-link"
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3] transition-colors hover:text-gold"
        >
          <ArrowLeft size={14} /> Catalogo
        </Link>
      </Reveal>

      <div className="mt-10 grid gap-14 lg:grid-cols-2">
        <Reveal>
          <div className="relative">
            {product.old_price && (
              <span className="absolute -left-0 top-0 z-10 bg-gold px-3 py-1.5 text-[9px] font-semibold uppercase tracking-[0.25em] text-black">
                In offerta
              </span>
            )}
            <Gallery images={galleryImages} name={product.name} />
          </div>
        </Reveal>

        <div className="flex flex-col justify-center">
          <Reveal delay={0.1}>
            <p className="text-[10px] uppercase tracking-[0.35em] text-gold">
              {CATEGORY_LABELS[product.category] || product.category}
            </p>
          </Reveal>
          <h1 className="mt-4 font-serif text-3xl leading-tight md:text-5xl">
            <MaskedLine delay={0.15}>{product.name}</MaskedLine>
          </h1>
          <Reveal delay={0.3}>
            <div className="mt-6 flex items-baseline gap-4">
              <p className="font-serif text-4xl text-gold" data-testid="product-detail-price">€ {fmtPrice(product.price)}</p>
              {product.old_price && (
                <p className="text-lg text-[#737373] line-through" data-testid="product-detail-old-price">€ {fmtPrice(product.old_price)}</p>
              )}
            </div>
          </Reveal>
          {product.material && (
            <Reveal delay={0.35}>
              <p className="mt-5 text-[11px] uppercase tracking-[0.3em] text-[#A3A3A3]" data-testid="product-detail-material">
                {product.material}
              </p>
            </Reveal>
          )}
          {product.description && (
            <Reveal delay={0.4}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-[#A3A3A3]" data-testid="product-detail-description">
                {product.description}
              </p>
            </Reveal>
          )}
          {product.reference && (
            <Reveal delay={0.45}>
              <p className="mt-4 text-[10px] uppercase tracking-[0.3em] text-[#737373]" data-testid="product-detail-reference">
                Cod. riferimento: <span className="text-gold-pale">{product.reference}</span>
              </p>
            </Reveal>
          )}
          <Reveal delay={0.5}>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href={waLink(waText)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="product-whatsapp-cta"
                className="inline-flex items-center gap-3 bg-gold px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-black transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-hover"
              >
                <MessageCircle size={15} />
                Richiedi info su WhatsApp
              </a>
              <a
                href={`tel:${STORES[0].tel}`}
                data-testid="product-call-cta"
                className="inline-flex items-center gap-3 border border-white/20 px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] transition-all duration-300 hover:-translate-y-0.5 hover:border-gold hover:text-gold"
              >
                <Phone size={15} />
                {STORES[0].phone}
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.55}>
            <p className="mt-5 text-[11px] italic leading-relaxed text-[#9c9c9c]" data-testid="product-detail-disclaimer">
              Le immagini sono dimostrative e rispecchiano fedelmente il gioiello in vendita nei nostri negozi.
            </p>
          </Reveal>
          <Reveal delay={0.6}>
            <div className="mt-10 flex items-center gap-2 border-t border-white/10 pt-6 text-[11px] uppercase tracking-[0.25em] text-[#737373]">
              <Star size={12} className="fill-gold text-gold" />
              Valutato 4.7 su Google · Disponibilità da confermare in negozio
            </div>
          </Reveal>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-28" data-testid="related-products-section">
          <MaskedLine className="font-serif text-3xl md:text-4xl">Potrebbe piacerti anche</MaskedLine>
          <div className="mt-10 grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
